/**
 * CommunityAd — rotating internal ad slot backed by /api/community/ads/rotate.
 * Fires impression on mount and click on CTA. Silently hides when no ad
 * is eligible so the layout never shows an empty rectangle.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Megaphone, ExternalLink } from 'lucide-react';

const CommunityAd = ({ placement = 'any', className = '' }) => {
  const { API } = useContext(AuthContext);
  const [ad, setAd] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/community/ads/rotate?placement=${placement}`);
        if (cancelled) return;
        setAd(r.data.ad || null);
        if (r.data.ad?.id) {
          // Fire-and-forget impression track
          axios.post(`${API}/community/ads/${r.data.ad.id}/impression`).catch(() => {});
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [API, placement]);

  if (!ad) return null;

  const isInternal = ad.cta_url?.startsWith('/');
  const handleClick = () => {
    if (ad.id) axios.post(`${API}/community/ads/${ad.id}/click`).catch(() => {});
  };

  const inner = (
    <div className="flex items-start gap-3">
      {ad.image_url ? (
        <img src={ad.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <Megaphone className="w-6 h-6" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">
          Sponsored
        </div>
        <div className="font-heading text-sm font-extrabold text-foreground line-clamp-1">{ad.title}</div>
        {ad.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ad.body}</p>
        )}
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary">
          {ad.cta_label || 'Learn more'} {!isInternal && <ExternalLink className="w-3 h-3" />}
        </span>
      </div>
    </div>
  );

  const commonClasses = `block pro-card p-3 border-l-4 border-primary/50 hover:border-primary hover:shadow-md transition-all ${className}`;

  if (isInternal) {
    return (
      <a href={ad.cta_url} onClick={handleClick} data-testid={`community-ad-${ad.id}`} className={commonClasses}>
        {inner}
      </a>
    );
  }
  return (
    <a
      href={ad.cta_url}
      target="_blank"
      rel="noreferrer sponsored"
      onClick={handleClick}
      data-testid={`community-ad-${ad.id}`}
      className={commonClasses}
    >
      {inner}
    </a>
  );
};

export default CommunityAd;
